import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        status: '',
        startDate: '',
        endDate: ''
    });
    const [statistics, setStatistics] = useState(null);
    const [error, setError] = useState(null);

    // API base URL - change this to match your backend
    const API_BASE_URL = 'https://theagencybazaar.com'; // Change to your backend URL

    useEffect(() => {
        fetchMessages();
        fetchStatistics();
    }, [filter.status, filter.startDate, filter.endDate]);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Build URL with full path
            let url = `${API_BASE_URL}/api/contact-messages`;
            const params = new URLSearchParams();
            
            if (filter.status && filter.status !== '') {
                params.append('status', filter.status);
            }
            if (filter.startDate && filter.startDate !== '') {
                params.append('startDate', filter.startDate);
            }
            if (filter.endDate && filter.endDate !== '') {
                params.append('endDate', filter.endDate);
            }
            
            if (params.toString()) {
                url += `?${params.toString()}`;
            }
            
            console.log('Fetching messages from:', url);
            
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 10000
            });
            
            console.log('Response:', response.data);
            
            if (response.data.success) {
                setMessages(response.data.data || []);
                console.log('Messages loaded:', response.data.data?.length || 0);
            } else {
                setError(response.data.message || 'Failed to fetch messages');
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            let errorMsg = 'Failed to fetch messages. ';
            
            if (error.code === 'ECONNABORTED') {
                errorMsg += 'Request timeout.';
            } else if (error.response) {
                errorMsg += error.response.data?.message || 'Server error.';
            } else if (error.request) {
                errorMsg += 'Cannot connect to server. Make sure backend is running on port 5000.';
            } else {
                errorMsg += error.message;
            }
            
            setError(errorMsg);
            Swal.fire('Error', errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const url = `${API_BASE_URL}/api/contact-messages/statistics/summary`;
            console.log('Fetching statistics from:', url);
            
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 10000
            });
            
            console.log('Statistics response:', response.data);
            
            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
            // Don't show error for statistics, just log it
        }
    };

    const updateStatus = async (messageId, status, adminNotes = '') => {
        try {
            const { value: notes } = await Swal.fire({
                title: 'Update Status',
                text: `Change status to ${status}`,
                input: 'textarea',
                inputLabel: 'Admin Notes (Optional)',
                inputPlaceholder: 'Add any notes here...',
                showCancelButton: true,
                confirmButtonText: 'Update',
                cancelButtonText: 'Cancel'
            });

            const url = `${API_BASE_URL}/api/contact-messages/${messageId}/status`;
            
            const response = await axios.put(url, {
                status: status,
                adminNotes: notes || ''
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                Swal.fire('Success', 'Message status updated', 'success');
                fetchMessages();
                fetchStatistics();
            }
        } catch (error) {
            console.error('Error updating status:', error);
            Swal.fire('Error', 'Failed to update status: ' + (error.response?.data?.message || error.message), 'error');
        }
    };

    const deleteMessage = async (messageId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const url = `${API_BASE_URL}/api/contact-messages/${messageId}`;
                const response = await axios.delete(url);
                
                if (response.data.success) {
                    Swal.fire('Deleted!', 'Message has been deleted.', 'success');
                    fetchMessages();
                    fetchStatistics();
                }
            } catch (error) {
                console.error('Error deleting message:', error);
                Swal.fire('Error', 'Failed to delete message: ' + (error.response?.data?.message || error.message), 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'Pending': 'badge bg-warning text-dark',
            'Read': 'badge bg-info',
            'Replied': 'badge bg-success',
            'Closed': 'badge bg-secondary'
        };
        return badges[status] || 'badge bg-light';
    };

    // Test function to check API connection
    const testAPIConnection = async () => {
        try {
            const testUrl = `${API_BASE_URL}/api/contact-messages/test`;
            console.log('Testing API at:', testUrl);
            const response = await axios.get(testUrl);
            console.log('API Test Response:', response.data);
            Swal.fire('API Test', 'API is working!', 'success');
        } catch (error) {
            console.error('API Test Failed:', error);
            Swal.fire('API Test Failed', `Cannot connect to ${API_BASE_URL}. Make sure backend is running.`, 'error');
        }
    };

    return (
        <div className="container mt-4">
            {/* Test Connection Button */}
            <div className="row mb-3">
                <div className="col-12">
                    <button 
                        className="btn btn-info btn-sm"
                        onClick={testAPIConnection}
                    >
                        Test API Connection
                    </button>
                </div>
            </div>

            <div className="row">
                {/* Statistics Cards */}
                {statistics && (
                    <div className="col-12 mb-4">
                        <div className="row">
                            <div className="col-md-2">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Total</h5>
                                        <h3>{statistics.TotalMessages || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-warning text-dark">
                                    <div className="card-body">
                                        <h5 className="card-title">Pending</h5>
                                        <h3>{statistics.PendingMessages || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-info text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Read</h5>
                                        <h3>{statistics.ReadMessages || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-success text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Replied</h5>
                                        <h3>{statistics.RepliedMessages || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-secondary text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Closed</h5>
                                        <h3>{statistics.ClosedMessages || 0}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card bg-dark text-white">
                                    <div className="card-body">
                                        <h5 className="card-title">Unique Users</h5>
                                        <h3>{statistics.UniqueUsers || 0}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="col-12 mb-4">
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-4">
                                    <label>Filter by Status</label>
                                    <select 
                                        className="form-control"
                                        value={filter.status}
                                        onChange={(e) => setFilter({...filter, status: e.target.value})}
                                    >
                                        <option value="">All</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Read">Read</option>
                                        <option value="Replied">Replied</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Start Date</label>
                                    <input 
                                        type="date" 
                                        className="form-control"
                                        value={filter.startDate}
                                        onChange={(e) => setFilter({...filter, startDate: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label>End Date</label>
                                    <input 
                                        type="date" 
                                        className="form-control"
                                        value={filter.endDate}
                                        onChange={(e) => setFilter({...filter, endDate: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label>&nbsp;</label>
                                    <button 
                                        className="btn btn-secondary form-control"
                                        onClick={() => setFilter({status: '', startDate: '', endDate: ''})}
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="col-12 mb-3">
                        <div className="alert alert-danger">
                            <strong>Error:</strong> {error}
                        </div>
                    </div>
                )}

                {/* Messages Table */}
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4>Contact Messages</h4>
                            <small className="text-muted">
                                {messages.length} message(s) found
                            </small>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p>Fetching messages...</p>
                                </div>
                            ) : messages.length === 0 ? (
                                <div className="alert alert-info">
                                    No messages found. 
                                    {!error && ' Submit a message through the contact form first.'}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Message</th>
                                                <th>Status</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {messages.map((message) => (
                                                <tr key={message.MessageID}>
                                                    <td>{message.MessageID}</td>
                                                    <td>{message.Name}</td>
                                                    <td>{message.Email}</td>
                                                    <td>{message.Phone}</td>
                                                    <td>
                                                        <button 
                                                            className="btn btn-sm btn-info"
                                                            onClick={() => {
                                                                Swal.fire({
                                                                    title: 'Message from ' + message.Name,
                                                                    html: `<div style="text-align: left;">
                                                                        <strong>Email:</strong> ${message.Email}<br/>
                                                                        <strong>Phone:</strong> ${message.Phone}<br/>
                                                                        <strong>Message:</strong><br/>
                                                                        ${message.Message}
                                                                    </div>`,
                                                                    icon: 'info',
                                                                    confirmButtonText: 'Close',
                                                                    width: '600px'
                                                                });
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span className={getStatusBadge(message.Status)}>
                                                            {message.Status}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(message.CreatedAt).toLocaleString()}</td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <button 
                                                                className="btn btn-sm btn-secondary dropdown-toggle"
                                                                type="button"
                                                                data-bs-toggle="dropdown"
                                                            >
                                                                Actions
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item"
                                                                        onClick={() => updateStatus(message.MessageID, 'Read')}
                                                                    >
                                                                        Mark as Read
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item"
                                                                        onClick={() => updateStatus(message.MessageID, 'Replied')}
                                                                    >
                                                                        Mark as Replied
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item"
                                                                        onClick={() => updateStatus(message.MessageID, 'Closed')}
                                                                    >
                                                                        Mark as Closed
                                                                    </button>
                                                                </li>
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <button 
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => deleteMessage(message.MessageID)}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminMessages;


